import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

const FullscreenZooplanktonModal = ({
  isOpen,
  onClose,
  zooplanktonOrganisms,
  locations,
  zooplanktonGridData,
  colorOverrides,
  updateZooplanktonValue,
  formatZooplanktonDisplayValue,
  setContextMenu
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-white">
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">Zooplankton - Fullskjerm</h2>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="relative overflow-auto">
              <table className="border-collapse" style={{minWidth: `${280 + locations.length * 90}px`}}>
                <thead className="sticky top-0 z-50">
                  <tr>
                    <th className="sticky left-0 z-[60] border-b border-slate-300 px-3 py-2 text-left text-sm font-semibold bg-slate-50" style={{width: '130px', minWidth: '130px'}}>
                      Prøvetype
                    </th>
                    <th className="sticky z-[60] border-b border-slate-300 px-3 py-2 text-left text-sm font-semibold bg-slate-50" style={{left: '130px', width: '150px', minWidth: '150px', boxShadow: '2px 0 0 0 #cbd5e1'}}>
                      Organisme
                    </th>
                    {locations.map((loc, index) => {
                      const letter = String.fromCharCode(65 + index);
                      const name = loc.split(' - ')[1];
                      return (
                        <th key={loc} className="border-r border-b border-slate-300 px-2 py-2 text-center text-sm font-semibold bg-white" style={{minWidth: '85px'}}>
                          <div className="text-xs font-bold">{letter} - {name}</div>
                          <div className="text-xs text-slate-500">(ant/m³)</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {zooplanktonOrganisms.map((organism, index) => {
                    const organismKey = `${organism.sampleType}_${organism.organism}`;
                    
                    return (
                      <tr key={organismKey} className="hover:bg-slate-50">
                        <td className="sticky left-0 z-40 border-b border-slate-300 px-3 py-2 text-sm bg-white" style={{width: '130px', minWidth: '130px'}}>
                          {organism.sampleType}
                        </td>
                        <td className="sticky border-b border-slate-300 px-3 py-2 text-sm italic bg-white" style={{zIndex: 40, left: '130px', width: '150px', minWidth: '150px', boxShadow: '2px 0 0 0 #cbd5e1'}}>
                          {organism.organism}
                        </td>
                        {locations.map(location => {
                          const cellKey = `${organismKey}_${location}`;
                          const hasColorOverride = colorOverrides[cellKey];
                          const colorClass = hasColorOverride 
                            ? hasColorOverride === 'green' ? 'bg-emerald-100' 
                              : hasColorOverride === 'yellow' ? 'bg-amber-100'
                              : hasColorOverride === 'red' ? 'bg-red-100'
                              : 'bg-emerald-100'
                            : 'bg-emerald-100'; // Default green for all zooplankton
                          
                          return (
                            <td 
                              key={location} 
                              className="border-r border-b border-slate-300 p-1"
                              onContextMenu={(e) => {
                                e.preventDefault();
                                e.stopPropagation(); // Prevent event from bubbling to row handler
                                
                                // Calculate menu position with viewport boundary checks
                                const menuWidth = 200;
                                const menuHeight = 300;
                                
                                let x = e.clientX;
                                let y = e.clientY;
                                
                                if (x + menuWidth > window.innerWidth) {
                                  x = window.innerWidth - menuWidth - 10;
                                }
                                
                                if (y + menuHeight > window.innerHeight) {
                                  y = window.innerHeight - menuHeight - 10;
                                }
                                
                                x = Math.max(10, x);
                                y = Math.max(10, y);
                                
                                setContextMenu({
                                  x,
                                  y,
                                  organismKey,
                                  location
                                });
                              }}
                            >
                              <Input 
                                value={formatZooplanktonDisplayValue(zooplanktonGridData[organismKey]?.[location] || '')}
                                onChange={(e) => updateZooplanktonValue(organismKey, location, e.target.value)}
                                className={`h-8 text-sm text-center border-0 focus:ring-1 focus:ring-teal-500 ${colorClass}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullscreenZooplanktonModal;
